import { ReactNode, useEffect, useRef } from "react";
import cc from "classcat";
import styles from "./BorderCard.module.scss";

type BorderCardProps = {
  title: string;
  children: ReactNode;
  /** Slides drawerChildren in from the right, dimming the body behind it. */
  isDrawerOpen?: boolean;
  onDrawerDismiss?: () => void;
  drawerChildren?: ReactNode;
};

const BorderCard = ({
  title,
  isDrawerOpen,
  onDrawerDismiss,
  drawerChildren,
  children
}: BorderCardProps) => {
  const drawerRef = useRef<HTMLDivElement>(null);

  // When opening a BorderCard drawer automatically focus the first focusable element.
  useEffect(() => {
    if (!isDrawerOpen) return;

    const drawer = drawerRef.current;
    if (!drawer) return;

    const firstFocusable = drawer.querySelector<HTMLElement>(
      [
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "[tabindex]:not([tabindex='-1'])"
      ].join(", ")
    );

    firstFocusable?.focus({ preventScroll: true });
  }, [isDrawerOpen]);

  return (
    <div className={styles.outer}>
      <div
        ref={drawerRef}
        role="dialog"
        className={cc([
          styles.drawer,
          isDrawerOpen ? styles["drawer--open"] : ""
        ])}
        data-testid="border-card-drawer"
        inert={!isDrawerOpen}
      >
        {drawerChildren}
      </div>
      <h4 className={styles.title}>{title}</h4>
      <div
        className={styles.bodyContainer}
        onClick={() => isDrawerOpen && onDrawerDismiss?.()}
      >
        <div
          className={cc([
            styles.body,
            isDrawerOpen ? styles["body--dimmed"] : ""
          ])}
          inert={isDrawerOpen}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default BorderCard;
